import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import clientformApiController from "services/clientforms.service";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { contentErrorMessage } from "./contentError";

const listErrorMessage = (action, fallback = "Unable to load client forms. Please try again.") => contentErrorMessage(action, fallback);

export const ClientformApi = createAsyncThunk("clientform/listview", async (formValues, thunkAPI) => {
  try {
    const resposedata = await clientformApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "listview"))
      .catch((error) => HandleError(thunkAPI, error, "listview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const ClientformDetailApi = createAsyncThunk("clientform/detail", async (formValues, thunkAPI) => {
  try {
    return await clientformApiController
      .view(formValues)
      .then((response) => HandleResponse(thunkAPI, response, "detail"))
      .catch((error) => HandleError(thunkAPI, error, "detail"));
  } catch (error) {
    return thunkAPI.rejectWithValue({ status: error?.response?.status || null, message: error?.response?.data || error.message });
  }
});

export const ClientFormProfileSyncApi = createAsyncThunk("clientform/profileSync", async (formValues, thunkAPI) => {
  try {
    return await clientformApiController
      .profileSync(formValues)
      .then((response) => HandleResponse(thunkAPI, response, "update"))
      .catch((error) => HandleError(thunkAPI, error, "update"));
  } catch (error) {
    return thunkAPI.rejectWithValue({ status: error?.response?.status || null, message: error?.response?.data || error.message });
  }
});

const profileSyncError = (action) => {
  const payload = action?.payload || {};
  const body = payload.message && typeof payload.message === "object" ? payload.message : payload;
  return {
    status: payload.status || body.status || null,
    message: body.message || payload.userMessage || "Unable to update the client profile.",
  };
};

const responseSubmission = (payload) => {
  const value = payload?.data || payload;
  return value?.submission || value;
};

const mergeSubmission = (current, payload) => {
  const incoming = responseSubmission(payload);
  if (!incoming || typeof incoming !== "object") return current;
  if (Array.isArray(incoming.answers) || Array.isArray(incoming.form_answer)) return incoming;
  if (Array.isArray(incoming.fields)) return { ...(current || {}), profile_sync: incoming };
  if (incoming.profile_sync || incoming.profileSync) return { ...(current || {}), ...incoming };
  return incoming;
};

const initialState = {
  isListView: [],
  listClientId: null,
  listRequestId: null,
  isListViewError: "",
  isclientDetailConsultationFormTab: "",
  isclientDetailConsultationForm: "",
  isProfileSyncError: "",
  isApiStatus: {
    ClientformApi: "",
    ClientformDetailApi: "",
    ClientFormProfileSyncApi: "",
  },
};

const clientformSlice = createSlice({
  name: "clientform",
  initialState,
  reducers: {
    reset: () => initialState,
    ClientDetailConsultationForm: (state, action) => {
      state.isclientDetailConsultationForm = action.payload;
    },
    ClientDetailConsultationFormTab: (state, action) => {
      state.isclientDetailConsultationFormTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ClientformApi.pending, (state, action) => {
        const clientId = Number(action.meta.arg?.client_id) || null;
        if (clientId !== state.listClientId) {
          state.isListView = { data: [] };
          state.listClientId = clientId;
        }
        state.listRequestId = action.meta.requestId;
        state.isApiStatus = { ...state.isApiStatus, ClientformApi: "loading" };
        state.isListViewError = "";
      })
      .addCase(ClientformApi.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        const previous = state.isListView;
        const isNextPage = previous && previous.current_page < action.payload.current_page;
        state.isListView = isNextPage && Array.isArray(previous.data) && Array.isArray(action.payload.data)
          ? { ...action.payload, data: [...previous.data, ...action.payload.data] }
          : action.payload;
        state.isApiStatus = { ...state.isApiStatus, ClientformApi: "succeeded" };
        state.isListViewError = "";
      })
      .addCase(ClientformApi.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.isApiStatus = { ...state.isApiStatus, ClientformApi: "failed" };
        state.isListViewError = listErrorMessage(action);
      })
      .addCase(ClientformDetailApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, ClientformDetailApi: "loading" };
        state.isProfileSyncError = "";
      })
      .addCase(ClientformDetailApi.fulfilled, (state, action) => {
        state.isclientDetailConsultationForm = responseSubmission(action.payload);
        state.isApiStatus = { ...state.isApiStatus, ClientformDetailApi: "succeeded" };
        state.isProfileSyncError = "";
      })
      .addCase(ClientformDetailApi.rejected, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, ClientformDetailApi: "failed" };
        state.isProfileSyncError = profileSyncError(action);
      })
      .addCase(ClientFormProfileSyncApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, ClientFormProfileSyncApi: "loading" };
        state.isProfileSyncError = "";
      })
      .addCase(ClientFormProfileSyncApi.fulfilled, (state, action) => {
        state.isclientDetailConsultationForm = mergeSubmission(state.isclientDetailConsultationForm, action.payload);
        state.isApiStatus = { ...state.isApiStatus, ClientFormProfileSyncApi: "succeeded" };
        state.isProfileSyncError = "";
      })
      .addCase(ClientFormProfileSyncApi.rejected, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, ClientFormProfileSyncApi: "failed" };
        state.isProfileSyncError = profileSyncError(action);
      });
  },
});
// Action creators are generated for each case reducer function
export const { reset, ClientDetailConsultationForm, ClientDetailConsultationFormTab } = clientformSlice.actions;
export default clientformSlice.reducer;
